import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  CreateSaleOwnershipSwapPayload,
  CreateSaleOwnershipSwapResponse,
  GetAllSaleOwnershipSwapsParams,
  GetSaleOwnershipSwapsParams,
  UpdateSaleOwnershipSwapPayload,
  UpdateSaleOwnershipSwapResponse,
  createSaleOwnershipSwap,
  getAllSaleOwnershipSwaps,
  getSaleOwnershipSwapReasons,
  getSaleOwnershipSwaps,
  updateSaleOwnershipSwap,
} from '~/services/SaleOwnershipSwapService';

import { orderQueryKeys } from './useGetOrder';

export const saleOwnershipSwapQueryKeys = {
  all: ['saleOwnershipSwaps'] as const,
  lists: () => [...saleOwnershipSwapQueryKeys.all, 'list'] as const,
  list: (filters: GetSaleOwnershipSwapsParams) => [...saleOwnershipSwapQueryKeys.lists(), filters] as const,
  byOrder: (orderId: string) => [...saleOwnershipSwapQueryKeys.all, 'order', orderId] as const,
  reasons: () => [...saleOwnershipSwapQueryKeys.all, 'reasons'] as const,
};

// Reasons are a fixed backend enum, so they only need fetching once per session.
const REASONS_STALE_TIME_MS = 60 * 60 * 1000;

export function useGetSaleOwnershipSwapReasons() {
  const query = useQuery({
    queryKey: saleOwnershipSwapQueryKeys.reasons(),
    queryFn: ({ signal }) => getSaleOwnershipSwapReasons(signal),
    staleTime: REASONS_STALE_TIME_MS,
  });

  const reasons = query.data?.result ?? [];
  const reasonLabels = Object.fromEntries(reasons.map(({ value, label }) => [value, label])) as Record<string, string>;

  return { ...query, reasons, reasonLabels };
}

export function useGetSaleOwnershipSwaps(params: GetSaleOwnershipSwapsParams) {
  return useQuery({
    queryKey: saleOwnershipSwapQueryKeys.list(params),
    queryFn: ({ signal }) => getSaleOwnershipSwaps(params, signal),
  });
}

export function useExportSaleOwnershipSwaps() {
  return useMutation({
    mutationFn: (params: GetAllSaleOwnershipSwapsParams) => getAllSaleOwnershipSwaps(params),
  });
}

export function useGetOrderSaleOwnershipSwaps(orderId: string) {
  return useQuery({
    queryKey: saleOwnershipSwapQueryKeys.byOrder(orderId),
    queryFn: ({ signal }) => getSaleOwnershipSwaps({ orderId }, signal),
    enabled: !!orderId,
  });
}

export function useCreateSaleOwnershipSwap() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<CreateSaleOwnershipSwapResponse, Error, CreateSaleOwnershipSwapPayload>({
    mutationFn: (payload) => createSaleOwnershipSwap(payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: saleOwnershipSwapQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.detail(response.result.orderId) });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to raise sale ownership swap: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateSaleOwnershipSwap() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<UpdateSaleOwnershipSwapResponse, Error, { id: string; payload: UpdateSaleOwnershipSwapPayload }>({
    mutationFn: ({ id, payload }) => updateSaleOwnershipSwap(id, payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: saleOwnershipSwapQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.detail(response.result.orderId) });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update sale ownership swap: ${error.message}`, { variant: 'error' });
      queryClient.invalidateQueries({ queryKey: saleOwnershipSwapQueryKeys.all });
    },
  });
}
